/**
 * eCommerce Preiskalkulator
 *
 * Standalone Tool zur Kalkulation von Verkaufspreisen
 * für verschiedene eCommerce-Plattformen.
 */

import { Calculator, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePriceCalculation } from '@/hooks/usePriceCalculation';
import {
  PlatformSelector,
  InputForm,
  PlatformFees,
  ResultsCard,
  CostBreakdown,
} from '@/components/v2/price-calculator';

export function PriceCalculator() {
  const { t } = useTranslation();
  const { input, result, updateField, setPlatform, reset } = usePriceCalculation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Calculator className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {t('pricing.title')}
            </h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {t('pricing.subtitle')}
            </p>
          </div>
        </div>
        <button
          onClick={reset}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
        >
          <RotateCcw className="h-4 w-4" />
          {t('pricing.reset')}
        </button>
      </div>

      {/* Platform Selector */}
      <div className="mb-6">
        <PlatformSelector selected={input.platformId} onSelect={setPlatform} />
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left: Inputs */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-sm border border-neutral-200 dark:border-neutral-700">
            <InputForm input={input} onUpdate={updateField} />
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-sm border border-neutral-200 dark:border-neutral-700">
            <PlatformFees input={input} onUpdate={updateField} />
          </div>

          <CostBreakdown result={result} input={input} />
        </div>

        {/* Right: Results */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 shadow-sm border border-neutral-200 dark:border-neutral-700 sticky top-24">
            <ResultsCard result={result} />
          </div>
        </div>
      </div>
    </div>
  );
}
